import { Deserializer } from "jsonapi-serializer";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Message } from "../models/Message";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { MessageWrapper } from "../models/wrappers/MessageWrapper";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const apiConnector = new APIConnector();

export const getMessages = async (listParameters?: ListQuery): Promise<MessageWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm)
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `longMessage?include=site&fields[longMessage]=lmMessage,site,lmDefault,isDefault&fields[site]=siteName${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    reject({status: response.status, message: genericError });
                }

                const messages: Message[] = data.map((message): Message => {
                    return {
                        id: message['id'],
                        name: message['lm-message'],
                        site: message['site']['site-name'],
                        default: message['lm-default'],
                        isDefault: message['is-default']
                    }
                })
                return resolve({ rows: messages, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getMessageById = async (messageId: string): Promise<Message> => {
    try {
        const messageFields = "fields[longMessage]=lmMessage,site,lmDefault,isDefault";
        const siteFields = "fields[site]=siteName";
        const url = `longMessage/${messageId}?include=site&${messageFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    reject({status: response.status, message: genericError });
                }

                const message: Message = {
                    id: data['id'],
                    name: data['lm-message'],
                    site: data['site']['site-name'],
                    siteId: data['site']['id'],
                    default: data['lm-default']
                }

                return resolve(message);
            })

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getMessageNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm)
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const url = `longMessage?fields[longMessage]=lmMessage,isDefault${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    reject({status: response.status, message: genericError });
                }

                const message: Name[] = data.map(message => {
                    return {
                        label: message['lm-message'],
                        value: message['id'],
                        isDefault: message['is-default']
                    }
                });
                return resolve({ rows: message, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error))
        })
    }
}

export const storeMessage = async (message: Message, createMessage = true) => {
    try {
        const newRecord = {
            data: {
                type: "longMessage",
                id: message.id,
                attributes: {
                    lmDefault: message.default ?? 0,
                    lmMessage: message.name,
                    lmNo: message.id
                },
                relationships: {
                    site: {
                        data: {
                            type: "site",
                            id: message.siteId
                        }
                    }
                }
            }
        }

        const url = `longMessage${createMessage ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createMessage, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createMessage) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, message) => {
                        if (error) {
                            return reject({status: response.status, id: message.id, success: false, message: genericError });
                        }
                        return resolve({ id: message.id, success: true });
                    })

                    return;
                }
                if (response.status === 204) {
                    return resolve({ id: message.id, success: true })
                }
                const content = await response.json();
                return reject({status: response.status, id: message.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({status: INTERNAL_SERVER_ERROR, id: message.id, success: false, message: genericError });

        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteMessage = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `longMessage/${id}`;
        const message = {
            data: {
                type: "longMessage",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, message)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
               return resolve({ id: id, success: true })
            }
            const json = await response.json();

            return reject({status: response.status, id: id, success: false,  message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

